import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { apiFetch } from "@/lib/api";
import { useInitialData } from "@/lib/initial-data-context";
import type { BlogPostSummary } from "@/lib/initial-data";

const BASE_SITE_URL = "https://www.tallahasseelassiepetcare.com";

function getPostDate(post: BlogPostSummary) {
  const createdAt = new Date(post.createdAt);
  const updatedAt = post.updatedAt ? new Date(post.updatedAt) : createdAt;
  const wasUpdated = updatedAt.getTime() > createdAt.getTime() + 1000;

  return {
    label: wasUpdated ? "Updated" : "Published",
    date: wasUpdated ? updatedAt : createdAt,
  };
}

export default function Blog() {
  const { blogPosts: initialPosts } = useInitialData();

  const [posts, setPosts] = useState<BlogPostSummary[]>(initialPosts ?? []);
  const [loading, setLoading] = useState(!initialPosts);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    if (initialPosts) return;
    apiFetch("/blog/posts")
      .then(setPosts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function getImageUrl(imagePath: string) {
    return `${BASE}/api/storage${imagePath}`;
  }

  function getAbsoluteImageUrl(imagePath: string) {
    return `${BASE_SITE_URL}/api/storage${imagePath}`;
  }

  const blogJsonLd = posts.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Tally Lassie Times",
    description: "Pet care blog by Tallahassee Lassie Pet Care",
    url: `${BASE_SITE_URL}/blog`,
    itemListElement: posts.map((post, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${BASE_SITE_URL}/blog/${post.slug}`,
      item: {
        "@type": "BlogPosting",
        headline: post.title,
        url: `${BASE_SITE_URL}/blog/${post.slug}`,
        datePublished: post.createdAt,
        ...(post.coverImage ? {
          image: post.coverImage.startsWith("/objects")
            ? getAbsoluteImageUrl(post.coverImage)
            : post.coverImage,
        } : {}),
        ...(post.excerpt ? { description: post.excerpt } : {}),
      },
    })),
  } : null;

  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Tally Lassie Times | Pet Care Blog"
        description="A pet education blog for dog moms and dads, cat moms and dads, pet sitters and dog walkers, and pet professionals, not limited to Tallahassee. Pet Sitting; Dog Walking; Tallahassee; Women Owned Business"
        path="/blog"
      />
      {blogJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonLd) }}
        />
      )}
      <div className="container mx-auto px-4 max-w-4xl">
        <Reveal>
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6">Tally Lassie Times</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Pet care updates and community news from our team in Tallahassee.
            </p>
          </div>
        </Reveal>

        {loading ? (
          <p className="text-center text-muted-foreground">Loading posts...</p>
        ) : posts.length === 0 ? (
          <Reveal>
            <div className="bg-primary/5 border border-primary/10 p-12 text-center" style={{ borderRadius: "32px 0 32px 0" }}>
              <p className="text-6xl mb-6">🐾</p>
              <h2 className="text-2xl font-serif font-bold mb-4">Coming Soon</h2>
              <p className="text-lg text-muted-foreground max-w-lg mx-auto mb-8">
                We're working on bringing you great content about pet care tips, community updates, and stories from the Tallahassee Lassie team. Check back soon!
              </p>
              <div className="flex justify-center gap-4">
                <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                  <a href="https://www.facebook.com/TallahasseeeLassiePetCare" target="_blank" rel="noopener noreferrer">
                    Follow Us on Facebook
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                  <a href="https://www.instagram.com/tallylassie" target="_blank" rel="noopener noreferrer">
                    Follow Us on Instagram
                  </a>
                </Button>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-8">
            {posts.map((post) => {
              const postDate = getPostDate(post);

              return (
                <Reveal key={post.id}>
                  <Link href={`/blog/${post.slug}`}>
                    <article className="bg-white border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" style={{ borderRadius: "16px 0 16px 0" }}>
                      <div className="flex flex-col md:flex-row">
                        {post.coverImage && (
                          <div className="md:w-1/3">
                            <img
                              src={post.coverImage.startsWith("/objects") ? getImageUrl(post.coverImage) : post.coverImage}
                              alt={post.title}
                              className="w-full h-48 md:h-full object-cover"
                            />
                          </div>
                        )}
                        <div className={`p-6 flex flex-col justify-center ${post.coverImage ? "md:w-2/3" : "w-full"}`}>
                          <p className="text-sm text-muted-foreground mb-2">
                            {postDate.label}{" "}
                            {postDate.date.toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                          <h2 className="text-2xl font-serif font-bold text-foreground mb-3">{post.title}</h2>
                          {post.excerpt && (
                            <p className="text-muted-foreground">{post.excerpt}</p>
                          )}
                          <span className="text-primary font-semibold text-sm mt-4 inline-block">Read More →</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
